import type { CreateProxyBody } from '../api/proxies';
import type { ProxyItem } from '../types';

export interface ProxyCopyDraft {
  proxyUserID: number | null;
  regionIds: number[];
  nodeId: number | null;
  port: number | null;
  note: string;
  expireDate: string;
  username: string;
  password: string;
}

type RandomValues = (values: Uint32Array) => Uint32Array;
type CredentialFactory = () => string;

export const generateSixDigitCredential = (
  randomValues: RandomValues = (values) => globalThis.crypto.getRandomValues(values),
): string => {
  const values = randomValues(new Uint32Array(6));
  return Array.from(values, (value) => String(value % 10)).join('');
};

export const buildProxyCopyDraft = (
  source: ProxyItem,
  credentialFactory: CredentialFactory = generateSixDigitCredential,
): ProxyCopyDraft => ({
  proxyUserID: source.proxy_user_id,
  regionIds: source.region_ids.length > 0 ? [...source.region_ids] : [source.region_id],
  nodeId: null,
  // Leave the port empty so the target node's available port is used.
  port: null,
  note: source.note || '',
  expireDate: source.expire_at.slice(0, 10),
  username: credentialFactory(),
  password: credentialFactory(),
});

export const isCopyCreateAvailable = (status: string): boolean =>
  status === 'active' || status === 'creating';

export interface CreateProxyFormInput {
  mode: 'create' | 'copy';
  proxyUserID: number;
  regionIds: number[];
  duration: string;
  expireDate: string;
  nodeId: number | null;
  port: number | null;
  note: string;
  username: string;
  password: string;
}

export const buildCreateProxyBody = (input: CreateProxyFormInput): CreateProxyBody => {
  const body: CreateProxyBody = {
    proxy_user_id: input.proxyUserID,
    region_ids: input.regionIds,
  };

  if (input.mode === 'copy') {
    body.expire_at = `${input.expireDate}T23:59:59+08:00`;
    body.username = input.username;
    body.password = input.password;
  } else {
    body.duration = input.duration;
  }

  if (input.nodeId != null) {
    body.node_id = input.nodeId;
  }
  if (input.port != null) {
    body.port = input.port;
  }
  const note = input.note.trim();
  if (note !== '') {
    body.note = note;
  }

  return body;
};
